import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Home, XCircle } from "lucide-react";
import { Helmet } from "react-helmet";
import { NavBar } from "../../components/navbar";
import { trackEvent } from "../../helpers";
import thumbGuide from "../../assets/thumb-guide.jpg";
import thumbToolkitFillable from "../../assets/thumb-toolkit-fillable.jpg";
import thumbToolkitPrintable from "../../assets/thumb-toolkit-printable.jpg";
import {
  PageWrapper,
  NavWrap,
  Wrap,
  DownloadHeader,
  HeaderContent,
  ReadyBadge,
  Title,
  Description,
  MetaStrip,
  MetaItem,
  MetaLabel,
  MetaValue,
  FilesList,
  FileItem,
  FileThumbnail,
  FileInfo,
  FileTag,
  FileName,
  FileDescription,
  FileSpec,
  DownloadButton,
  NextSteps,
  NextCard,
  CardLabel,
  CardTitle,
  CardDescription,
  Footer,
  FooterContent,
  LoadingState,
  Spinner,
  ErrorState,
  ErrorIcon,
  ErrorTitle,
  ErrorMessage,
  ErrorInfo,
  HomeButton,
} from "./download.styles";

interface DownloadData {
  email: string;
  productSlugs: string[];
  expiresAt: string;
  downloadCount: number;
}

interface ProductFile {
  tag: string;
  name: string;
  description: string;
  spec: string;
  filename: string;
  thumbnail: string;
  isPrimary?: boolean;
}

const PRODUCT_FILES: Record<string, ProductFile[]> = {
  "unhinged-guide": [
    {
      tag: "01 · The guide",
      name: "The Unhinged Guide to Breaking Into Tech",
      description: "The complete 20-chapter guide, from choosing a direction to landing the offer.",
      spec: "PDF · 687 pages · 2.8 MB",
      filename: "The-Unhinged-Guide-to-Breaking-Into-Tech.pdf",
      thumbnail: thumbGuide,
      isPrimary: true,
    },
    {
      tag: "02 · Toolkit, fillable",
      name: "The Unhinged Toolkit: Fillable",
      description: "All 22 worksheets and templates. Type straight into them on screen and save your answers.",
      spec: "Fillable PDF · 271 pages · 3.2 MB",
      filename: "The-Unhinged-Toolkit-Fillable.pdf",
      thumbnail: thumbToolkitFillable,
    },
    {
      tag: "03 · Toolkit, printable",
      name: "The Unhinged Toolkit: Printable",
      description: "The same 22 tools, set up for printing and filling in by hand.",
      spec: "PDF · A4 · 0.8 MB",
      filename: "The-Unhinged-Toolkit-Printable-Worksheets.pdf",
      thumbnail: thumbToolkitPrintable,
    },
  ],
};

export const DownloadPortal = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadData, setDownloadData] = useState<DownloadData | null>(null);
  const [isMenuIconClicked, setIsMenuIconClicked] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
  const token = searchParams.get("token");

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1024);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError("No download token provided");
        setLoading(false);
        return;
      }

      try {
        const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3002";
        const response = await fetch(`${apiUrl}/api/download/${token}`);

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error("Invalid download link");
          } else if (response.status === 410) {
            throw new Error("This download link has expired");
          } else {
            throw new Error("Failed to verify download link");
          }
        }

        const data = await response.json();
        setDownloadData(data);

        // Track successful download page view
        trackEvent('download_page_viewed', {
          email: data.email,
          products: data.productSlugs,
          downloadCount: data.downloadCount,
          expiresAt: data.expiresAt,
        });
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "An error occurred";
        setError(errorMessage);

        // Track failed download page access
        trackEvent('download_page_view_failed', {
          error: errorMessage,
          token: token ? token.substring(0, 8) + '...' : 'missing',
        });
      } finally {
        setLoading(false);
      }
    };

    verifyToken();
  }, [token]);

  const getFiles = (): ProductFile[] => {
    if (!downloadData) return [];

    const allFiles: ProductFile[] = [];
    downloadData.productSlugs.forEach((slug) => {
      const files = PRODUCT_FILES[slug];
      if (files) {
        allFiles.push(...files);
      }
    });

    return allFiles;
  };

  const handleDownload = (filename: string, fileTag: string) => {
    // Track file download
    trackEvent('file_downloaded', {
      filename: filename,
      fileType: fileTag,
      email: downloadData?.email,
      currentDownloadCount: downloadData?.downloadCount || 0,
    });

    const link = document.createElement("a");
    link.href = `/unhinged-toolkit/${filename}`;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Your downloads · Renée Louise</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <PageWrapper>
          <NavWrap>
            <NavBar
              variant="dark"
              isValidRoute={true}
              isResumePage={false}
              isMobile={isMobile}
              isMenuIconClicked={isMenuIconClicked}
              setIsMenuIconClicked={setIsMenuIconClicked}
            />
          </NavWrap>
          <Wrap>
            <LoadingState>
              <Spinner />
              <p>Verifying your download link...</p>
            </LoadingState>
          </Wrap>
        </PageWrapper>
      </>
    );
  }

  if (error || !downloadData) {
    return (
      <>
        <Helmet>
          <title>Access denied · Renée Louise</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <PageWrapper>
          <NavWrap>
            <NavBar
              variant="dark"
              isValidRoute={true}
              isResumePage={false}
              isMobile={isMobile}
              isMenuIconClicked={isMenuIconClicked}
              setIsMenuIconClicked={setIsMenuIconClicked}
            />
          </NavWrap>
          <Wrap>
            <ErrorState>
              <ErrorIcon>
                <XCircle size={48} />
              </ErrorIcon>
              <ErrorTitle>Access Denied</ErrorTitle>
              <ErrorMessage>
                {error || "Unable to access download portal"}
              </ErrorMessage>
              <ErrorInfo>
                If you believe this is an error, please contact
                hello@reneelouise.tech with your order ID.
              </ErrorInfo>
              <HomeButton href="/">
                <Home size={20} />
                Return to Home
              </HomeButton>
            </ErrorState>
          </Wrap>
        </PageWrapper>
      </>
    );
  }

  const files = getFiles();

  return (
    <>
      <Helmet>
        <title>Your downloads · Renée Louise</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <PageWrapper>
        <NavWrap>
          <NavBar
            variant="dark"
            isValidRoute={true}
            isResumePage={false}
            isMobile={isMobile}
            isMenuIconClicked={isMenuIconClicked}
            setIsMenuIconClicked={setIsMenuIconClicked}
          />
        </NavWrap>

        <Wrap>
          <DownloadHeader>
            <HeaderContent>
              <ReadyBadge>Ready to download</ReadyBadge>
              <Title>
                Your <em>downloads.</em>
              </Title>
              <Description>
                Welcome back. Everything you bought is below. Save the files
                somewhere you'll find them again.
              </Description>
            </HeaderContent>
          </DownloadHeader>

          <MetaStrip aria-label="Download details">
            <MetaItem>
              <MetaLabel>Sent to</MetaLabel>
              <MetaValue>{downloadData.email}</MetaValue>
            </MetaItem>
            <MetaItem>
              <MetaLabel>Link expires</MetaLabel>
              <MetaValue>{formatDate(downloadData.expiresAt)}</MetaValue>
            </MetaItem>
            <MetaItem>
              <MetaLabel>Times accessed</MetaLabel>
              <MetaValue>{downloadData.downloadCount}</MetaValue>
            </MetaItem>
          </MetaStrip>

          <FilesList>
            {files.map((file, index) => (
              <FileItem key={index} $isPrimary={file.isPrimary}>
                <FileThumbnail
                  as="div"
                  style={{
                    width: '88px',
                    height: '124px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <img
                    src={file.thumbnail}
                    alt=""
                    width="88"
                    height="124"
                    loading="lazy"
                  />
                </FileThumbnail>
                <FileInfo>
                  <FileTag>{file.tag}</FileTag>
                  <FileName>{file.name}</FileName>
                  <FileDescription>{file.description}</FileDescription>
                  <FileSpec>{file.spec}</FileSpec>
                </FileInfo>
                <DownloadButton
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleDownload(file.filename, file.tag);
                  }}
                  download={file.filename}
                >
                  Download
                  <svg viewBox="0 0 16 16" aria-hidden="true">
                    <path d="M8 2v9M4 7l4 4 4-4M2 14h12" />
                  </svg>
                </DownloadButton>
              </FileItem>
            ))}
          </FilesList>

          <NextSteps>
            <NextCard>
              <CardLabel>Start here</CardLabel>
              <CardTitle>Chapter 1, then Tool 01</CardTitle>
              <CardDescription>
                Read "Why the Hell Do You Want to Work in Tech?", then complete
                the Find Your Why Worksheet. For the fillable toolkit, use Adobe
                Acrobat Reader, Chrome or Preview, and <b>Save</b> (not Print to
                PDF) to keep your answers.
              </CardDescription>
            </NextCard>
            <NextCard>
              <CardLabel>Need help?</CardLabel>
              <CardTitle>Something not working?</CardTitle>
              <CardDescription>
                You can re-download anytime before the link expires. For anything
                else, email{" "}
                <a href="mailto:hello@reneelouise.tech">
                  hello@reneelouise.tech
                </a>
                .
              </CardDescription>
            </NextCard>
          </NextSteps>
        </Wrap>

        <Footer>
          <FooterContent>
            <span>© 2026 Renée Louise</span>
            <span>Now go do the thing.</span>
          </FooterContent>
        </Footer>
      </PageWrapper>
    </>
  );
};
